import {
    HistoryEduRounded as HistoryEduRoundedIcon,
    LocalLibraryOutlined as LocalLibraryOutlinedIcon,
} from '@mui/icons-material';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { CourseHistoryItem } from '~/shared/data-contract/course-history';

import { CardIconCaption } from '../../../../design/components/cards/CardIconCaption';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { useCourseHistoryQuery } from '../../../query-hooks/users/course-history';
import { useUserHistoryOptionsQuery } from '../../../query-hooks/users/user-history';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { createUserCourseHistoryUrl, editUserCourseHistoryUrl } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';
import { CardPlaceholder } from './common/CardPlaceholder';
import { HistoryItem } from './common/HistoryItem';
import { HistoryItemListWrapper } from './common/HistoryItemListWrapper';
import { ShowAllButton } from './common/ShowAllButton';

const CourseHistoryCaption: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <CardIconCaption
            color="learningOpportunity.main"
            icon={LocalLibraryOutlinedIcon}
            text={translate('pages.user-course-history.caption')}
        />
    );
};

type CourseHistoryContentProps = {
    readonly canUserAddCourseHistory?: boolean;
    readonly canUserEditCourseHistory?: boolean;
    readonly courseHistory?: ReadonlyArray<CourseHistoryItem>;
    readonly userId?: string;
};

export const CourseHistoryContent: FunctionComponent<CourseHistoryContentProps> = ({
    canUserAddCourseHistory = false,
    canUserEditCourseHistory = false,
    courseHistory = [],
    userId,
}) => {
    const { actions, translate } = useContext(ApplicationContext);

    const [showAll, setShowAll] = useState(false);
    const hasCourseHistory = courseHistory.length > 0;
    const slicedCourses = showAll ? courseHistory : courseHistory.slice(0, 2);

    const headlineActions = canUserAddCourseHistory && is.nonEmptyString(userId) && (
        <AddButton onClick={() => actions.navigateTo(createUserCourseHistoryUrl(userId))} />
    );

    const editAction = (itemId: string): VoidFunction | undefined =>
        canUserEditCourseHistory && is.nonEmptyString(userId)
            ? () => actions.navigateTo(editUserCourseHistoryUrl(userId, itemId))
            : undefined;

    useScrollToLocationHash();

    return (
        <HistoryItemListWrapper
            anchorId="course-history"
            headline={translate('pages.recruiter-profile.background.course-history.title')}
            headlineActions={headlineActions}
        >
            {hasCourseHistory ? (
                slicedCourses.map((courseItem) => (
                    <HistoryItem
                        key={courseItem.id}
                        caption={<CourseHistoryCaption />}
                        dateFrom={courseItem.dateFrom}
                        dateTo={courseItem.dateTo}
                        description={courseItem.description}
                        skills={courseItem.skills}
                        skillsHeadline={translate('pages.user-course-history.skills-list-headline')}
                        subTitle={[courseItem.school]}
                        title={courseItem.courseTitle}
                        onClickEdit={editAction(courseItem.id)}
                    />
                ))
            ) : (
                <CardPlaceholder
                    placeholderIcon={<HistoryEduRoundedIcon />}
                    placeholderText={translate('pages.recruiter-profile.background.course-history.placeholder')}
                />
            )}
            {courseHistory.length > 2 && <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />}
        </HistoryItemListWrapper>
    );
};

type CourseHistoryProps = {
    readonly userId: string;
};

export const CourseHistory: FunctionComponent<CourseHistoryProps> = ({ userId }: CourseHistoryProps) => {
    const { data: userHistoryOptions, isFetching: userHistoryOptionsIsFetching } = useUserHistoryOptionsQuery(userId);
    const { data: courseHistory, isFetching: courseHistoryIsFetching } = useCourseHistoryQuery(
        userId,
        userHistoryOptions?.canReadHistory ?? false,
    );

    if (userHistoryOptionsIsFetching || courseHistoryIsFetching) {
        return <InlineLoadingSpinner />;
    }

    return (
        <CourseHistoryContent
            canUserAddCourseHistory={userHistoryOptions?.canEditHistory}
            canUserEditCourseHistory={userHistoryOptions?.canEditHistory}
            courseHistory={courseHistory}
            userId={userId}
        />
    );
};
